import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Atajos de teclado globales del shell.
 *
 * El panel de ayuda (`KeyboardShortcuts.jsx`, tecla `?`) anunciaba **12 atajos y
 * funcionaban 2**: `⌘K` y `?`. Los otros nueve se pintaban con su separador
 * "then" y no tenían manejador en ninguna parte. Este hook los implementa.
 *
 * ## Secuencias
 *
 * `G` y `N` abren una secuencia: la siguiente tecla, dentro de la ventana,
 * resuelve el destino. Es el modelo de GitHub, Linear y Superhuman, y es el que
 * el panel ya describía.
 *
 * ## Qué NO captura
 *
 * - Cualquier tecla escrita en un campo editable (`input`, `textarea`, `select`,
 *   `contentEditable`). Sin esto, teclear "gastos" en un buscador navegaría.
 * - Cualquier tecla con un diálogo de Radix abierto: el foco vive dentro y las
 *   secuencias sueltas no deben sacar al usuario de un formulario a medias.
 * - Combinaciones con modificador, salvo el propio `⌘/` `Ctrl+/`. Así no se
 *   pisan los atajos del navegador.
 */

/** Ventana para completar una secuencia. Por encima de ~1,5 s deja de leerse
 *  como "dos teclas seguidas" y empieza a capturar pulsaciones no relacionadas. */
const SEQUENCE_WINDOW_MS = 1200;

/** `G` + tecla → ir a. Los destinos son paths REALES, no alias:
 *  `/proyectos` es un `<Navigate>` a `/projects` y `/clientes` otro a
 *  `/contactos`, y esas redirecciones descartan el query string. */
const GO_TO = {
  h: '/home',
  i: '/facturas',
  c: '/contactos',
  p: '/projects',
  s: '/configuracion',
};

/** `N` + tecla → crear. `?action=new` lo consume `useOpenOnActionNew` en la
 *  pantalla de destino, que es lo que abre el formulario al aterrizar. */
const NEW_IN = {
  i: '/factura-form',
  e: '/gastos?action=new',
  c: '/contactos?action=new',
};

function isTypingTarget(target) {
  if (!target || typeof target.closest !== 'function') return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

function hasOpenDialog() {
  return Boolean(document.querySelector('[role="dialog"][data-state="open"]'));
}

/**
 * @param {object}   options
 * @param {() => void} options.onToggleAi  Abre/cierra el panel de ARIA (`⌘/`).
 * @param {boolean}  [options.enabled]     Permite apagarlos por completo.
 */
export function useKeyboardShortcuts({ onToggleAi, enabled = true } = {}) {
  const navigate = useNavigate();

  // Los callbacks se leen por ref para no re-suscribir el listener en cada
  // render del Layout, que re-renderiza con cada cambio de ruta.
  const onToggleAiRef = useRef(onToggleAi);
  onToggleAiRef.current = onToggleAi;

  const pendingRef = useRef(null); // 'g' | 'n' | null
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const clearPending = () => {
      pendingRef.current = null;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const handler = (event) => {
      if (isTypingTarget(event.target)) return;

      const key = event.key?.toLowerCase();
      if (!key) return;

      // ⌘/ · Ctrl+/ — panel de ARIA. Se atiende incluso con una secuencia a
      // medias: lleva modificador, así que no puede ser el segundo paso de una.
      if (key === '/' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        clearPending();
        onToggleAiRef.current?.();
        return;
      }

      // El resto son teclas sueltas: con modificador, es del navegador.
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (hasOpenDialog()) return;

      // Segundo paso de una secuencia abierta.
      if (pendingRef.current) {
        const table = pendingRef.current === 'g' ? GO_TO : NEW_IN;
        const target = table[key];
        clearPending();
        if (target) {
          event.preventDefault();
          navigate(target);
        }
        return;
      }

      // Primer paso.
      if (key === 'g' || key === 'n') {
        pendingRef.current = key;
        timerRef.current = setTimeout(clearPending, SEQUENCE_WINDOW_MS);
        return;
      }

      // Escape cancela una secuencia a medias sin efectos colaterales.
      if (key === 'escape') clearPending();
    };

    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
      clearPending();
    };
  }, [enabled, navigate]);
}

export default useKeyboardShortcuts;
